// OnboardingQuestionnaire — the required first-run questionnaire (migration 261).
//
// Shown to a NEW free account (created on/after the server's
// onboardingRequiredSince) before the map, until it is answered. There is no
// close button, Escape does nothing and the backdrop is inert: the founder's
// decision (2026-10-26) after 3 of the first 4 real users skipped the old
// optional prompt and so never answered "what are you here to do".
//
// Three screens, every question a tap, ~45 seconds:
//   1. About you      goal + experience
//   2. Your goals     deals you expect, biggest challenge, how you find deals today
//   3. Where          the zip you want to buy in (+ how you heard, if not asked yet)
// The zip turns on one new-listing alert and unlocks every property in it.
//
// Fail-open, on purpose: if saving fails twice (server/network, not a bad
// answer) a "Continue for now" button appears so an outage never locks a new
// user out of the product. It is not persisted, so the questionnaire returns
// on the next load. A user cannot reach it by choosing not to answer.
//
// The value line on screen 2 is a comparison, not a promise: one closed deal
// can pay for five years of Parcely, and results vary.

import React, { useEffect, useRef, useState } from "react";
import { capture } from "../lib/analytics";
import { putProfileOnboarding, SignupIntent, SignupSource } from "../api";
import { useFocusTrap } from "../hooks/useFocusTrap";

type Experience = "none" | "1_2" | "3_10" | "10_plus";
type DealsExpected = "0_1" | "2_3" | "4_10" | "10_plus";
type Challenge = "finding_leads" | "judging_deals" | "reaching_owners" | "funding" | "closing" | "time";
type Tool = "spreadsheets" | "paid_list_service" | "courthouse" | "agent_mls" | "nothing" | "other";

export const GOAL_CHOICES: ReadonlyArray<{ code: SignupIntent; label: string }> = [
  { code: "auction_bidder", label: "Bid at foreclosure auctions" },
  { code: "wholesaler", label: "Find off-market sellers to wholesale" },
  { code: "flipper_landlord", label: "Buy to flip or rent" },
  { code: "agent", label: "I'm an agent working with buyers" },
  { code: "researching", label: "Just researching" },
];
export const EXPERIENCE_CHOICES: ReadonlyArray<{ code: Experience; label: string }> = [
  { code: "none", label: "No deals yet" },
  { code: "1_2", label: "1 to 2 deals" },
  { code: "3_10", label: "3 to 10 deals" },
  { code: "10_plus", label: "More than 10" },
];
export const DEALS_CHOICES: ReadonlyArray<{ code: DealsExpected; label: string }> = [
  { code: "0_1", label: "1 deal" },
  { code: "2_3", label: "2 to 3 deals" },
  { code: "4_10", label: "4 to 10 deals" },
  { code: "10_plus", label: "More than 10" },
];
export const CHALLENGE_CHOICES: ReadonlyArray<{ code: Challenge; label: string }> = [
  { code: "finding_leads", label: "Finding good leads" },
  { code: "judging_deals", label: "Knowing which are worth it" },
  { code: "reaching_owners", label: "Reaching the owner" },
  { code: "funding", label: "Funding the deal" },
  { code: "closing", label: "Getting to closing" },
  { code: "time", label: "Finding the time" },
];
export const TOOL_CHOICES: ReadonlyArray<{ code: Tool; label: string }> = [
  { code: "spreadsheets", label: "Spreadsheets and my own research" },
  { code: "paid_list_service", label: "A paid list or data service" },
  { code: "courthouse", label: "Courthouse and legal notices" },
  { code: "agent_mls", label: "An agent or the MLS" },
  { code: "other", label: "Something else" },
  { code: "nothing", label: "Nothing yet" },
];
export const SOURCE_CHOICES: ReadonlyArray<{ code: Exclude<SignupSource, "skipped">; label: string }> = [
  { code: "facebook_reia", label: "Facebook or a REIA group" },
  { code: "google", label: "Google search" },
  { code: "letter", label: "A letter in the mail" },
  { code: "referral", label: "A friend or colleague" },
  { code: "agent", label: "A real-estate agent" },
  { code: "other", label: "Somewhere else" },
];

interface Props {
  /** True when the user has not yet been asked "how did you hear about us". */
  askSource: boolean;
  /** Called after a successful save, with the zip that is now unlocked. */
  onDone: (welcomeZip: string) => void;
  /** Fail-open escape after repeated server/network failures (not persisted). */
  onContinueWithoutSaving: () => void;
  /** True when the user has no county yet: the zip step cannot save without one. */
  needsCounty?: boolean;
  /** Opens the county picker. */
  onPickCounty?: () => void;
  /**
   * True while the county picker is open. The questionnaire stays mounted (so
   * nothing the user typed is lost) but draws nothing until the picker closes.
   */
  suspended?: boolean;
}

const SCREENS = 3;

const Chips = <T extends string>({
  label,
  choices,
  value,
  onPick,
  multi = false,
  hint,
  fieldId,
}: {
  label: string;
  choices: ReadonlyArray<{ code: T; label: string }>;
  value: T | T[] | null;
  onPick: (code: T) => void;
  multi?: boolean;
  /** Set after a Continue with this question unanswered: names what to do. */
  hint?: string;
  fieldId: string;
}) => {
  const picked = (code: T) => (Array.isArray(value) ? value.includes(code) : value === code);
  const hintId = `onboarding-q-hint-${fieldId}`;
  return (
    <fieldset className={`onboarding-q-field${hint ? " onboarding-q-field--missed" : ""}`} data-q-field={fieldId}>
      <legend className="onboarding-q-label">{label}</legend>
      <div
        className="attribution-choices"
        role={multi ? "group" : "radiogroup"}
        aria-label={label}
        aria-invalid={hint ? true : undefined}
        aria-describedby={hint ? hintId : undefined}
      >
        {choices.map((c) => (
          <button
            key={c.code}
            type="button"
            role={multi ? undefined : "radio"}
            aria-checked={multi ? undefined : picked(c.code)}
            aria-pressed={multi ? picked(c.code) : undefined}
            className={`attribution-chip${picked(c.code) ? " attribution-chip--active" : ""}`}
            onClick={() => onPick(c.code)}
          >
            {c.label}
          </button>
        ))}
      </div>
      {hint && (
        <p className="onboarding-q-hint" id={hintId}>
          {hint}
        </p>
      )}
    </fieldset>
  );
};

const OnboardingQuestionnaire: React.FC<Props> = ({ askSource, onDone, onContinueWithoutSaving, needsCounty = false, onPickCounty, suspended = false }) => {
  const [screen, setScreen] = useState(0);
  const [goal, setGoal] = useState<SignupIntent | null>(null);
  const [experience, setExperience] = useState<Experience | null>(null);
  const [deals, setDeals] = useState<DealsExpected | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [tools, setTools] = useState<Tool[]>([]);
  const [zip, setZip] = useState("");
  const [source, setSource] = useState<Exclude<SignupSource, "skipped"> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState(0);
  const [saved, setSaved] = useState<{ zip: string } | null>(null);
  // Set when Continue is pressed with a question unanswered; hints are derived
  // from the live answers, so answering a question clears its own hint.
  const [showMissed, setShowMissed] = useState(false);
  // The server said "county_required" (the parent may not know yet).
  const [countyRequired, setCountyRequired] = useState(false);
  // The user finished the questions and was sent to the county picker: when it
  // closes with a county chosen, the answers are saved without another click.
  const [awaitingCounty, setAwaitingCounty] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  // No onEscape: the questionnaire cannot be dismissed.
  const trapRef = useFocusTrap<HTMLDivElement>({ active: !suspended });

  useEffect(() => {
    capture("onboarding_q_step_viewed", { step: saved ? SCREENS + 1 : screen + 1, of: SCREENS });
  }, [screen, saved]);

  const toggleTool = (t: Tool) =>
    setTools((cur) => {
      if (t === "nothing") return cur.includes("nothing") ? [] : ["nothing"];
      const without = cur.filter((x) => x !== "nothing");
      return without.includes(t) ? without.filter((x) => x !== t) : [...without, t];
    });

  const zipOk = /^\d{5}$/.test(zip.trim());
  // The questions on this screen that still need an answer, in page order.
  // `hint` names what to do; none of this changes what is saved.
  const missing: { id: string; hint: string }[] = [];
  if (screen === 0) {
    if (!goal) missing.push({ id: "goal", hint: "Pick what you're here to do." });
    if (!experience) missing.push({ id: "experience", hint: "Pick how many deals you've closed." });
  } else if (screen === 1) {
    if (!deals) missing.push({ id: "deals", hint: "Pick how many deals you hope to do in the next 12 months." });
    if (!challenge) missing.push({ id: "challenge", hint: "Pick your biggest challenge." });
    if (tools.length === 0) missing.push({ id: "tools", hint: "Pick at least one, or choose Nothing yet." });
  } else {
    if (!zipOk) missing.push({ id: "zip", hint: "Enter a 5-digit zip code." });
    if (askSource && !source) missing.push({ id: "source", hint: "Pick how you heard about Parcely." });
  }
  const hintFor = (id: string) => (showMissed ? missing.find((m) => m.id === id)?.hint : undefined);
  const needCounty = screen === SCREENS - 1 && (needsCounty || countyRequired);

  const save = async () => {
    if (busy || !goal || !experience || !deals || !challenge || tools.length === 0 || !zipOk) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await putProfileOnboarding({
        intent: goal,
        experience,
        dealsExpected: deals,
        challenge,
        tools,
        zip: zip.trim(),
        ...(askSource && source ? { source } : {}),
      });
      capture("onboarding_q_completed", { deals_expected: deals, experience, intent: goal });
      setSaved({ zip: resp.welcomeZip });
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.error === "county_required") setCountyRequired(true);
      if (status === 422 || status === 409 || status === 400) {
        // The user's answer is the problem (zip outside their county, county
        // not picked yet): say so and let them fix it. Not an outage.
        setError(data?.message || "Check your answers and try again.");
      } else {
        setFailures((n) => n + 1);
        setError("Couldn't save that. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  // Back from the county picker: save if a county was chosen, otherwise stay
  // on the zip step (the button offers the picker again).
  const pickerWasOpen = useRef(false);
  useEffect(() => {
    if (!awaitingCounty) {
      pickerWasOpen.current = false;
      return;
    }
    if (suspended) {
      pickerWasOpen.current = true;
      return;
    }
    if (!pickerWasOpen.current) return; // the picker has not opened yet
    setAwaitingCounty(false);
    if (!needsCounty) {
      setCountyRequired(false);
      void save();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingCounty, suspended, needsCounty]);

  // Bring the first unanswered question into view and focus it: a screen can
  // be taller than the viewport, so the missing one may be off-screen.
  const focusFirstMissed = (id: string) => {
    const root = modalRef.current;
    if (!root) return;
    const target =
      id === "zip"
        ? root.querySelector<HTMLElement>("#onboarding-q-zip")
        : root.querySelector<HTMLElement>(`[data-q-field="${id}"] button`);
    target?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    target?.focus({ preventScroll: true });
  };

  const next = () => {
    if (busy) return;
    if (missing.length > 0) {
      setShowMissed(true);
      focusFirstMissed(missing[0].id);
      return;
    }
    if (needCounty) {
      // Every question is answered; the county comes last, then this saves.
      setAwaitingCounty(true);
      onPickCounty?.();
      return;
    }
    if (screen < SCREENS - 1) {
      setScreen(screen + 1);
      setShowMissed(false);
      setError(null);
    } else {
      void save();
    }
  };

  if (suspended) return null;

  return (
    <div className="auth-backdrop" data-testid="onboarding-q-backdrop">
      <div
        className="upgrade-modal onboarding-q-modal"
        ref={(el) => {
          trapRef.current = el;
          modalRef.current = el;
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-q-title"
      >
        {saved ? (
          <>
            <h2 className="upgrade-title" id="onboarding-q-title">
              You're set
            </h2>
            <ul className="onboarding-q-wins">
              <li>
                <strong>{saved.zip} is unlocked.</strong> Every property there opens in full: owner, equity and distress signals.
              </li>
              <li>
                <strong>Your alert is on.</strong> We'll email you when a new property lists in {saved.zip}.
              </li>
              <li>Tap any property on the map to see everything we know about it.</li>
            </ul>
            <div className="upgrade-actions">
              <button type="button" className="upgrade-btn-primary" onClick={() => onDone(saved.zip)} autoFocus>
                Show me {saved.zip}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="onboarding-q-progress" role="progressbar" aria-valuemin={1} aria-valuemax={SCREENS} aria-valuenow={screen + 1}>
              {Array.from({ length: SCREENS }, (_, i) => (
                <span key={i} className={`onboarding-q-seg${i <= screen ? " onboarding-q-seg--on" : ""}`} />
              ))}
            </div>
            <h2 className="upgrade-title" id="onboarding-q-title">
              {screen === 0 ? "Let's set Parcely up for you" : screen === 1 ? "What do you want out of it?" : "Where do you want to buy?"}
            </h2>

            {screen === 0 && (
              <>
                <Chips label="What are you here to do?" choices={GOAL_CHOICES} value={goal} onPick={setGoal} fieldId="goal" hint={hintFor("goal")} />
                <Chips label="How many deals have you closed?" choices={EXPERIENCE_CHOICES} value={experience} onPick={setExperience} fieldId="experience" hint={hintFor("experience")} />
              </>
            )}

            {screen === 1 && (
              <>
                <Chips label="How many deals do you hope to do in the next 12 months?" choices={DEALS_CHOICES} value={deals} onPick={setDeals} fieldId="deals" hint={hintFor("deals")} />
                <p className="onboarding-q-callout">
                  One closed deal can pay for five years of Parcely: Scout is $45 a month, $2,700 over five years. Results vary and no deal is
                  guaranteed.
                </p>
                <Chips label="What's your biggest challenge?" choices={CHALLENGE_CHOICES} value={challenge} onPick={setChallenge} fieldId="challenge" hint={hintFor("challenge")} />
                <Chips label="How do you find deals today? (pick any)" choices={TOOL_CHOICES} value={tools} onPick={toggleTool} multi fieldId="tools" hint={hintFor("tools")} />
              </>
            )}

            {screen === 2 && (
              <>
                <p className="upgrade-reason">
                  Enter the zip code you want to buy in. We'll unlock every property in it and email you the moment a new one lists.
                </p>
                <label htmlFor="onboarding-q-zip" className="onboarding-q-label">
                  Zip code
                </label>
                <input
                  id="onboarding-q-zip"
                  className="county-picker-search"
                  type="text"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={5}
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="28036"
                  autoFocus
                  aria-invalid={hintFor("zip") ? true : undefined}
                  aria-describedby={hintFor("zip") ? "onboarding-q-hint-zip" : undefined}
                />
                {hintFor("zip") && (
                  <p className="onboarding-q-hint" id="onboarding-q-hint-zip">
                    {hintFor("zip")}
                  </p>
                )}
                {needCounty && (
                  <p className="onboarding-q-hint onboarding-q-hint--county" role="status">
                    Last step: you'll choose your free county next. Your zip has to be inside it.
                  </p>
                )}
                {askSource && (
                  <Chips label="How did you hear about Parcely?" choices={SOURCE_CHOICES} value={source} onPick={setSource} fieldId="source" hint={hintFor("source")} />
                )}
              </>
            )}

            {/* One live region: a save error, else a screen-reader summary naming
                the first unanswered question (the visible hint sits beside it). */}
            {error && !needCounty ? (
              <p className="county-picker-error" role="alert">
                {error}
              </p>
            ) : (
              <p className="onboarding-q-sr" role="alert">
                {showMissed && missing.length > 0 ? `Please answer: ${missing[0].hint}` : ""}
              </p>
            )}
            <div className="upgrade-actions">
              {screen > 0 && (
                <button type="button" className="upgrade-btn-secondary" onClick={() => setScreen(screen - 1)} disabled={busy}>
                  Back
                </button>
              )}
              <button type="button" className="upgrade-btn-primary" onClick={next} disabled={busy}>
                {busy ? "Saving…" : needCounty ? "Choose my county" : screen === SCREENS - 1 ? "Unlock my zip" : "Continue"}
              </button>
            </div>
            {failures >= 2 && (
              <button type="button" className="setup-call-fallback" onClick={onContinueWithoutSaving}>
                Having trouble? Continue for now
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingQuestionnaire;
